import { WizardActionsRHF } from '@app/modules/audit/modules/aod/screens/aod-rules-add/wizard/actions/WizardActionsRhf';
import { yupResolver } from '@hookform/resolvers/yup';
import { auditReports } from '@mi-front/api-clients';
import { Seconds } from '@mi-front/core';
import { Duration } from '@mi-front/ui';
import { Box, Grid, Typography } from '@mui/material';
import { ViewLoader } from '@ui/utils';
import React from 'react';
import { FormContainer, SelectElement } from 'react-hook-form-mui';
import { FormattedMessage } from 'react-intl';
import { gipExportIncludeAudioValues, useGipExportSetup } from './useGipExportSetup';
import { GipPathFormatCustomTextField } from './GipPathFormatCustomTextField';

export interface FormFieldsProps {
  initialConfig?: auditReports.GipEvidenceExportOptionsDto;
  reportType: string;
  onSubmit: (data: auditReports.GipEvidenceExportOptionsDto) => Promise<void>;
  ContentWrapper?: React.ComponentType<{ children: React.ReactNode }>;
  actions?: React.ReactNode;
  inDialog?: boolean;
  children?: React.ReactNode;
}

export function GipEvidenceExportConfigForm({
  initialConfig,
  reportType,
  onSubmit,
  ContentWrapper = React.Fragment,
  actions,
  inDialog = false,
  children,
}: FormFieldsProps) {
  const setup = useGipExportSetup(initialConfig, reportType);

  if (!setup) {
    return <ViewLoader />;
  }

  return (
    <FormContainer
      defaultValues={initialConfig ?? setup.initialValues}
      resolver={yupResolver(setup.validationSchema)}
      onSuccess={onSubmit}
    >
      <ContentWrapper>
        <Grid container spacing={2}>
          <Grid item xl={inDialog ? undefined : 3} lg={inDialog ? undefined : 4} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 3 }}>
              <FormattedMessage
                id="audit.gipReport.export.filesConfig"
                defaultMessage="Configuración de archivos"
              />
            </Typography>

            <Box>
              <Box mb={2}>
                <SelectElement
                  name="audioMatchIncludeBefore"
                  label={
                    <FormattedMessage
                      id="audit.gipReport.export.audioMatchIncludeBeforeLabel"
                      defaultMessage="Tiempo a incluir antes del match"
                    />
                  }
                  options={gipExportIncludeAudioValues.map((opt) => ({
                    id: opt,
                    label: <Duration seconds={opt as Seconds} />,
                  }))}
                  fullWidth
                  required
                />
              </Box>
              <Box mb={2}>
                <SelectElement
                  name="audioMatchIncludeAfter"
                  label={
                    <FormattedMessage
                      id="audit.gipReport.export.audioMatchIncludeAfterLabel"
                      defaultMessage="Tiempo a incluir después del match"
                    />
                  }
                  options={gipExportIncludeAudioValues.map((opt) => ({
                    id: opt,
                    label: <Duration seconds={opt as Seconds} />,
                  }))}
                  fullWidth
                  required
                />
              </Box>
              {setup.pathFormats && (
                <Box mb={2}>
                  <SelectElement
                    name="pathFormat.formatId"
                    label={
                      <FormattedMessage
                        id="audit.gipReport.export.pathFormat"
                        defaultMessage="Formato de path"
                      />
                    }
                    options={setup.pathFormats}
                    fullWidth
                    required
                  />
                </Box>
              )}
              <GipPathFormatCustomTextField pathFormats={setup.pathFormats} />
            </Box>
          </Grid>
          <Grid item xl={inDialog ? undefined : 3} lg={inDialog ? undefined : 4} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 3 }}>
              <FormattedMessage
                id="audit.gipReport.export.xlsConfig"
                defaultMessage="Configuración de XLS"
              />
            </Typography>
            <Box>
              <Box mb={2}>
                {setup.definitions.length > 0 ? (
                  <SelectElement
                    name="definitionId"
                    label={
                      <FormattedMessage
                        id="audit.gipReport.export.definition"
                        defaultMessage="Plantilla"
                      />
                    }
                    options={setup.definitions}
                    labelKey="name"
                    fullWidth
                    required
                  />
                ) : (
                  <Box>
                    <FormattedMessage
                      id="audit.gipReport.export.noTemplatesAvailable"
                      defaultMessage="No hay plantillas disponibles"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
        {children}
      </ContentWrapper>
      {actions ? actions : <WizardActionsRHF />}
    </FormContainer>
  );
}
